import { toErrorMessage } from "@wakeloop/utils";
import { getDaemonStatus } from "../config/daemon-state";
import type { WakeLoopLockMeta } from "../config/lock";
import {
  captureDaemonServiceEvidenceBestEffort,
  type DaemonServiceFailureStage,
  formatDaemonFailureContextText,
} from "../local-service/lifecycle/daemon-service-evidence";
import { getDaemonSystemServiceLogPath } from "../local-service/lifecycle/daemon-service-manager";
import { isVersionDowngrade } from "../local-service/lifecycle/version-change-direction";
import {
  formatInlineWakeLoopCliCommands,
  formatWakeLoopCliCommand,
  formatWakeLoopCliCommands,
} from "../runtime/cli-entry-command";
import { emitCliJsonEnvelope } from "../runtime/cli-json-output";
import type { RuntimeContext } from "../runtime/context";
import type { createPresenter, Presenter } from "../runtime/presenter/index";
import {
  formatDaemonDowngradeBlockedNextStep,
  formatDaemonServiceDowngradeBlockedRecommendation,
  getDaemonDowngradeBlockedTitle,
} from "../system/local-service-install-attention";
import { sanitizeSystemServiceErrorMessage } from "./service-error-text";
import { renderServiceActionCard } from "./service-output";
import type {
  DaemonRunFailureReason,
  DaemonRunReason,
} from "./service-run-result";
import type { SystemServiceStartupFailureVerdict } from "./service-start-failure";

export type DaemonServiceMaintenanceAction = "repair" | "update";

export type DaemonReinstallStartBlockedReason = Extract<
  DaemonRunFailureReason,
  | "cleanup_required"
  | "authentication_required"
  | "authentication_environment_mismatch"
  | "service_dependencies_unavailable"
>;

export class DaemonReinstallUnexpectedStartError extends Error {
  override readonly cause: unknown;
  readonly diagnosticDetail: string | null;
  readonly outcome: Extract<DaemonReinstallOutcome, { result: "start_failed" }>;

  constructor(input: {
    cause: unknown;
    desiredState: "background" | "stopped";
    diagnosticDetail: string | null;
    forceUsed: boolean;
  }) {
    super(toErrorMessage(input.cause));
    this.name = "DaemonReinstallUnexpectedStartError";
    this.cause = input.cause;
    this.diagnosticDetail = input.diagnosticDetail;
    this.outcome = createDaemonReinstallStartFailedOutcome({
      backupDataPath: null,
      desiredState: input.desiredState,
      failureStage: "reinstall-install",
      forceUsed: input.forceUsed,
    });
  }
}

export class DaemonReinstallFailureError extends Error {
  override readonly cause: unknown;
  readonly backupDataPath: string | null;
  readonly evidencePath: string | null;
  readonly failureStage: DaemonServiceFailureStage | null;

  constructor(input: {
    backupDataPath: string | null;
    cause: unknown;
    evidencePath?: string | null;
    failureStage?: DaemonServiceFailureStage | null;
  }) {
    super(toErrorMessage(input.cause));
    this.name = "DaemonReinstallFailureError";
    this.backupDataPath = input.backupDataPath;
    this.cause = input.cause;
    this.evidencePath = input.evidencePath ?? null;
    this.failureStage = input.failureStage ?? null;
  }
}

export type DaemonReinstallTimeoutPolicy = "abort" | "prompt_force" | "force";

export type DaemonReinstallDrainTimeoutResult = {
  ok: false;
  result: "drain_timeout";
  forceRequired: true;
  waitMs: number;
};

export type DaemonReinstallServiceState = "running" | "stopped";

export type DaemonReinstallCompletedOutcome = {
  ok: true;
  result: "completed";
  backupDataPath: string | null;
  desiredState: "background" | "stopped";
  evidencePath?: string | null;
  forceUsed: boolean;
  serviceState: DaemonReinstallServiceState;
};

export type DaemonReinstallStoppedSafeOutcome = {
  ok: true;
  result: "stopped_safe";
  desiredState: "background" | "stopped";
  backupDataPath: null;
  evidencePath?: string | null;
  failureStage?: DaemonServiceFailureStage | null;
  forceUsed: boolean;
  serviceState: "stopped";
  errorMessage: string;
};

export type DaemonReinstallOutcome =
  | {
      ok: false;
      result: "cancelled";
    }
  | {
      ok: false;
      result: "in_progress";
      heldBy: string | null;
      retryCommand: string;
      startedAt: string | null;
    }
  | DaemonReinstallDrainTimeoutResult
  | {
      ok: false;
      result: "start_blocked";
      blockReason: DaemonReinstallStartBlockedReason;
      desiredState: "background" | "stopped";
      forceUsed: boolean;
      backupDataPath: string | null;
      startFailureVerdict?: SystemServiceStartupFailureVerdict | null;
    }
  | {
      ok: false;
      result: "start_failed";
      desiredState: "background" | "stopped";
      forceUsed: boolean;
      backupDataPath: string | null;
      failureStage?: DaemonServiceFailureStage | null;
      evidencePath?: string | null;
      startFailureVerdict?: SystemServiceStartupFailureVerdict | null;
    }
  | {
      ok: false;
      result: "downgrade_blocked";
      installedVersion: string;
      requestedVersion: string;
      upgradeCommand: string;
    }
  | DaemonReinstallStoppedSafeOutcome
  | DaemonReinstallCompletedOutcome;

export function formatDaemonReinstallLockHolder(
  lockMeta: WakeLoopLockMeta | null | undefined
): string | null {
  if (!lockMeta) {
    return null;
  }

  const pid = typeof lockMeta.pid === "number" ? lockMeta.pid : null;
  const sessionId =
    typeof lockMeta.sessionId === "string" ? lockMeta.sessionId.trim() : null;

  if (pid !== null && sessionId) {
    return `WakeLoop process pid ${String(pid)} (${sessionId})`;
  }
  if (pid !== null) {
    return `WakeLoop process pid ${String(pid)}`;
  }
  if (sessionId) {
    return `WakeLoop session ${sessionId}`;
  }
  return null;
}

export function formatDaemonReinstallLockStartedAt(
  lockMeta: WakeLoopLockMeta | null | undefined
): string | null {
  if (!lockMeta) {
    return null;
  }
  const startedAt =
    typeof lockMeta.at === "string" ? lockMeta.at.trim() : undefined;
  return startedAt && startedAt.length > 0 ? startedAt : null;
}

export function createDaemonReinstallInProgressOutcome(
  lockMeta?: WakeLoopLockMeta | null,
  action: DaemonServiceMaintenanceAction = "repair"
): Extract<DaemonReinstallOutcome, { result: "in_progress" }> {
  return {
    ok: false,
    result: "in_progress",
    heldBy: formatDaemonReinstallLockHolder(lockMeta),
    retryCommand: formatDaemonServiceMaintenanceCommand(action),
    startedAt: formatDaemonReinstallLockStartedAt(lockMeta),
  };
}

export function createDaemonReinstallDowngradeBlockedOutcome(input: {
  installedVersion: string;
  requestedVersion: string;
}): Extract<DaemonReinstallOutcome, { result: "downgrade_blocked" }> {
  return {
    ok: false,
    result: "downgrade_blocked",
    installedVersion: input.installedVersion,
    requestedVersion: input.requestedVersion,
    upgradeCommand: "wakeloop upgrade",
  };
}

export function normalizeDaemonServiceMaintenanceAction(
  action: DaemonServiceMaintenanceAction | undefined
): DaemonServiceMaintenanceAction {
  return action === "update" ? "update" : "repair";
}

export function formatDaemonServiceMaintenanceCommand(
  action: DaemonServiceMaintenanceAction,
  suffix = ""
): string {
  return `wakeloop service ${action}${suffix}`;
}

export function formatDaemonServiceMaintenanceActionLabel(
  action: DaemonServiceMaintenanceAction
): "repair" | "update" {
  return action;
}

export function formatDaemonServiceMaintenanceActionTitle(
  action: DaemonServiceMaintenanceAction
): "Repair" | "Update" {
  return action === "update" ? "Update" : "Repair";
}

export function formatDaemonServiceMaintenanceActionPast(
  action: DaemonServiceMaintenanceAction
): "repaired" | "updated" {
  return action === "update" ? "updated" : "repaired";
}

export function formatDaemonServiceMaintenanceActionPastTitle(
  action: DaemonServiceMaintenanceAction
): "Repaired" | "Updated" {
  return action === "update" ? "Updated" : "Repaired";
}

export function formatDaemonServiceMaintenanceActionGerund(
  action: DaemonServiceMaintenanceAction
): "repairing" | "updating" {
  return action === "update" ? "updating" : "repairing";
}

export function createDaemonReinstallCompletedOutcome(input: {
  backupDataPath: string | null;
  desiredState: "background" | "stopped";
  evidencePath?: string | null;
  forceUsed: boolean;
  serviceState: DaemonReinstallServiceState;
}): DaemonReinstallCompletedOutcome {
  return {
    ok: true,
    result: "completed",
    backupDataPath: input.backupDataPath,
    desiredState: input.desiredState,
    ...(input.evidencePath ? { evidencePath: input.evidencePath } : {}),
    forceUsed: input.forceUsed,
    serviceState: input.serviceState,
  };
}

export function createDaemonReinstallStoppedSafeOutcome(input: {
  desiredState: "background" | "stopped";
  evidencePath?: string | null;
  failureStage?: DaemonServiceFailureStage | null;
  forceUsed: boolean;
  errorMessage: string;
}): DaemonReinstallStoppedSafeOutcome {
  return {
    ok: true,
    result: "stopped_safe",
    desiredState: input.desiredState,
    backupDataPath: null,
    ...(input.evidencePath ? { evidencePath: input.evidencePath } : {}),
    ...(input.failureStage ? { failureStage: input.failureStage } : {}),
    forceUsed: input.forceUsed,
    serviceState: "stopped",
    errorMessage: input.errorMessage,
  };
}

export function createDaemonReinstallStartFailedOutcome(input: {
  backupDataPath: string | null;
  desiredState: "background" | "stopped";
  evidencePath?: string | null;
  failureStage?: DaemonServiceFailureStage | null;
  forceUsed: boolean;
  startFailureVerdict?: SystemServiceStartupFailureVerdict | null;
}): Extract<DaemonReinstallOutcome, { result: "start_failed" }> {
  return {
    ok: false,
    result: "start_failed",
    desiredState: input.desiredState,
    forceUsed: input.forceUsed,
    backupDataPath: input.backupDataPath,
    ...(input.failureStage ? { failureStage: input.failureStage } : {}),
    ...(input.evidencePath ? { evidencePath: input.evidencePath } : {}),
    ...(input.startFailureVerdict
      ? { startFailureVerdict: input.startFailureVerdict }
      : {}),
  };
}

export function createDaemonReinstallStartBlockedOutcome(input: {
  backupDataPath: string | null;
  blockReason: DaemonReinstallStartBlockedReason;
  desiredState: "background" | "stopped";
  forceUsed: boolean;
  startFailureVerdict?: SystemServiceStartupFailureVerdict | null;
}): Extract<DaemonReinstallOutcome, { result: "start_blocked" }> {
  return {
    ok: false,
    result: "start_blocked",
    blockReason: input.blockReason,
    desiredState: input.desiredState,
    forceUsed: input.forceUsed,
    backupDataPath: input.backupDataPath,
    ...(input.startFailureVerdict
      ? { startFailureVerdict: input.startFailureVerdict }
      : {}),
  };
}

export async function attachDaemonReinstallEvidence(input: {
  outcome: DaemonReinstallOutcome;
  runtime: RuntimeContext;
}): Promise<DaemonReinstallOutcome> {
  if (input.outcome.result !== "start_failed" || input.outcome.evidencePath) {
    return input.outcome;
  }
  const evidencePath = await captureDaemonServiceEvidenceBestEffort({
    runtime: input.runtime,
  });
  if (!evidencePath) {
    return input.outcome;
  }
  return {
    ...input.outcome,
    evidencePath,
  };
}

export function buildDaemonReinstallFailureMessage(input: {
  action: DaemonServiceMaintenanceAction;
  backupDataPath: string | null;
  errorMessage: string;
  failureStage?: DaemonServiceFailureStage | null;
  evidencePath: string | null;
  includeDiagnosticDetails?: boolean;
  resolvedView: RuntimeContext["resolvedView"];
}): string {
  const actionLabel = formatDaemonServiceMaintenanceActionLabel(input.action);
  const actionCommand = formatDaemonServiceMaintenanceCommand(input.action);
  if (
    input.resolvedView === "human" &&
    input.includeDiagnosticDetails !== true
  ) {
    if (input.errorMessage.includes("test fixture identity")) {
      return formatInlineWakeLoopCliCommands(
        [
          `WakeLoop Service ${actionLabel} failed because this production lane contains old test setup identity that cannot be reused.`,
          "Rerun the setup command from WakeLoop Web so WakeLoop can write a fresh production service contract.",
          "For diagnostics, run `wakeloop service status --view agent`.",
        ].join(" ")
      );
    }
    return formatInlineWakeLoopCliCommands(
      `WakeLoop Service ${actionLabel} failed. Run \`${actionCommand}\`. For diagnostics, run \`wakeloop service status --view agent\`.`
    );
  }

  const failureContext = formatDaemonFailureContextText({
    backupDataPath: input.backupDataPath,
    evidencePath: input.evidencePath,
    failureStage: input.failureStage ?? null,
  });
  return failureContext.length > 0
    ? `WakeLoop Service ${actionLabel} failed: ${input.errorMessage}. ${failureContext}`
    : `WakeLoop Service ${actionLabel} failed: ${input.errorMessage}.`;
}

export function isDaemonReinstallCompletedAndRunning(
  outcome: DaemonReinstallOutcome
): outcome is DaemonReinstallCompletedOutcome & { serviceState: "running" } {
  return (
    outcome.ok &&
    outcome.result === "completed" &&
    outcome.serviceState === "running"
  );
}

export function isDaemonReinstallCompletedAndStopped(
  outcome: DaemonReinstallOutcome
): outcome is DaemonReinstallCompletedOutcome & { serviceState: "stopped" } {
  return (
    outcome.ok &&
    outcome.result === "completed" &&
    outcome.serviceState === "stopped"
  );
}

export function resolveDaemonReinstallStartBlockedReason(
  reason: DaemonRunReason
): DaemonReinstallStartBlockedReason | null {
  switch (reason) {
    case "cleanup_required":
    case "authentication_required":
    case "authentication_environment_mismatch":
    case "service_dependencies_unavailable":
      return reason;
    default:
      return null;
  }
}

export function buildDaemonReinstallResultTitle(
  outcome: DaemonReinstallOutcome,
  action: DaemonServiceMaintenanceAction
): string {
  const actionTitle = formatDaemonServiceMaintenanceActionTitle(action);
  const actionPastTitle = formatDaemonServiceMaintenanceActionPastTitle(action);
  switch (outcome.result) {
    case "completed":
      return outcome.serviceState === "running"
        ? `✅ WakeLoop Service ${actionPastTitle}`
        : `⚠️ WakeLoop Service ${actionPastTitle} But Not Running`;
    case "stopped_safe":
      return "✅ WakeLoop Service Safely Stopped";
    case "in_progress":
      return `⏳ WakeLoop Service ${actionTitle} In Progress`;
    case "downgrade_blocked":
      return `⛔ WakeLoop Service ${actionTitle} Blocked`;
    case "drain_timeout":
      return `⏸️ WakeLoop Service ${actionTitle} Paused`;
    case "start_blocked":
      return `⚠️ WakeLoop Service ${actionPastTitle} But Start Is Blocked`;
    case "start_failed":
      return `⚠️ WakeLoop Service ${actionPastTitle} But Not Running`;
    case "cancelled":
      return `WakeLoop Service ${actionTitle} Cancelled`;
    default: {
      const unexpectedOutcome: never = outcome;
      throw new Error(
        `Unexpected daemon reinstall outcome: ${String(unexpectedOutcome)}`
      );
    }
  }
}

export function buildDaemonReinstallWhyText(
  outcome: Exclude<DaemonReinstallOutcome, { result: "cancelled" }>,
  action: DaemonServiceMaintenanceAction
): string {
  const forcedSuffix =
    "forceUsed" in outcome && outcome.forceUsed
      ? " Force continue was used after graceful drain timed out."
      : "";
  const actionPast = formatDaemonServiceMaintenanceActionPast(action);
  const actionGerund = formatDaemonServiceMaintenanceActionGerund(action);
  switch (outcome.result) {
    case "completed":
      return outcome.serviceState === "running"
        ? `WakeLoop ${actionPast} WakeLoop Service and restored background service for this lane.${forcedSuffix}`
        : `${buildDaemonReinstallStoppedServiceMessage(action)}${forcedSuffix}`;
    case "stopped_safe":
      return `WakeLoop ${actionPast} WakeLoop Service, but it could not safely restore the previous running state. WakeLoop kept the current version installed and stopped instead of rolling back to older local service state.${forcedSuffix}`;
    case "in_progress":
      return `Another WakeLoop process is already ${actionGerund} WakeLoop Service on this device.`;
    case "downgrade_blocked":
      return "The installed WakeLoop Service version is newer than this CLI version.";
    case "drain_timeout":
      return `Graceful drain did not finish within ${String(outcome.waitMs)}ms, so WakeLoop did not force the existing service to stop.`;
    case "start_blocked":
      return buildDaemonReinstallStartBlockedWhyText(outcome, action);
    case "start_failed":
      return `WakeLoop ${actionPast} WakeLoop Service, but background startup did not reach a healthy running state for this lane.${forcedSuffix}`;
    default: {
      const unexpectedOutcome: never = outcome;
      throw new Error(
        `Unexpected daemon reinstall outcome while building why text: ${String(
          unexpectedOutcome
        )}`
      );
    }
  }
}

export function buildDaemonReinstallCurrentStateText(
  outcome: Exclude<DaemonReinstallOutcome, { result: "cancelled" }>,
  action: DaemonServiceMaintenanceAction
): string {
  const actionGerund = formatDaemonServiceMaintenanceActionGerund(action);
  switch (outcome.result) {
    case "completed":
      return outcome.serviceState === "running"
        ? "WakeLoop Service is running in the background for this lane."
        : "WakeLoop Service is installed, but it is not running on this computer.";
    case "stopped_safe":
      return formatInlineWakeLoopCliCommands(
        "WakeLoop Service is installed at the current version and safely stopped for this lane. For diagnostics, run `wakeloop service status --view agent`."
      );
    case "in_progress": {
      const details = [outcome.heldBy, outcome.startedAt].filter(Boolean);
      return details.length > 0
        ? details.join(" • ")
        : `Another WakeLoop process is currently ${actionGerund} WakeLoop Service on this device.`;
    }
    case "downgrade_blocked":
      return `Installed WakeLoop Service: v${outcome.installedVersion}. This CLI requested: v${outcome.requestedVersion}.`;
    case "drain_timeout":
      return `The existing WakeLoop Service was left in place, and ${formatDaemonServiceMaintenanceActionLabel(
        action
      )} did not continue.`;
    case "start_blocked":
      return buildDaemonReinstallStartBlockedCurrentStateText(outcome);
    case "start_failed":
      return formatInlineWakeLoopCliCommands(
        "WakeLoop Service is installed, but it is not running for this lane. For diagnostics, run `wakeloop service status --view agent`."
      );
    default: {
      const unexpectedOutcome: never = outcome;
      throw new Error(
        `Unexpected daemon reinstall outcome while building current state: ${String(
          unexpectedOutcome
        )}`
      );
    }
  }
}

export function buildDaemonReinstallNextStepText(
  outcome: Exclude<DaemonReinstallOutcome, { result: "cancelled" }>,
  action: DaemonServiceMaintenanceAction
): string {
  switch (outcome.result) {
    case "completed":
      return outcome.serviceState === "running"
        ? "No action is required."
        : formatInlineWakeLoopCliCommands(
            `Run \`${formatWakeLoopCliCommand(
              "wakeloop service start"
            )}\` to keep WakeLoop Service running in the background. If you need live logs, run \`${formatWakeLoopCliCommand(
              "wakeloop service start --mode foreground"
            )}\`.`
          );
    case "stopped_safe":
      return formatInlineWakeLoopCliCommands(
        `Run \`${formatWakeLoopCliCommand(
          "wakeloop service start --mode foreground"
        )}\` to inspect startup live, or \`${formatWakeLoopCliCommand(
          "wakeloop service start"
        )}\` to retry background startup from the current version.`
      );
    case "in_progress":
      return formatInlineWakeLoopCliCommands(
        `Wait a moment. If WakeLoop Service is still not connected, run \`${formatWakeLoopCliCommand(
          outcome.retryCommand
        )}\` again.`
      );
    case "downgrade_blocked":
      return `${formatDaemonServiceDowngradeBlockedRecommendation({
        installedVersion: outcome.installedVersion,
        expectedVersion: outcome.requestedVersion,
      })} ${formatDaemonDowngradeBlockedNextStep()}`;
    case "drain_timeout":
      return formatInlineWakeLoopCliCommands(
        `Re-run \`${formatWakeLoopCliCommand(
          formatDaemonServiceMaintenanceCommand(action, " --force")
        )}\` to continue.`
      );
    case "start_blocked":
      return buildDaemonReinstallStartBlockedNextStep(outcome, action);
    case "start_failed":
      return buildDaemonReinstallStartFailureNextStep(action);
    default: {
      const unexpectedOutcome: never = outcome;
      throw new Error(
        `Unexpected daemon reinstall outcome while building next step: ${String(
          unexpectedOutcome
        )}`
      );
    }
  }
}

export function buildDaemonReinstallHeadlineText(
  outcome: Exclude<DaemonReinstallOutcome, { result: "cancelled" }>,
  action: DaemonServiceMaintenanceAction
): string {
  const actionPast = formatDaemonServiceMaintenanceActionPast(action);
  const actionLabel = formatDaemonServiceMaintenanceActionLabel(action);
  switch (outcome.result) {
    case "completed":
      return outcome.serviceState === "running"
        ? `WakeLoop Service was ${actionPast} and is now running.`
        : buildDaemonReinstallStoppedServiceMessage(action);
    case "stopped_safe":
      return `WakeLoop Service was ${actionPast} and left safely stopped on the current version.`;
    case "in_progress":
      return `WakeLoop Service ${actionLabel} is already running.`;
    case "downgrade_blocked":
      return `WakeLoop Service ${actionLabel} is blocked.`;
    case "drain_timeout":
      return `WakeLoop Service ${actionLabel} paused before it could continue.`;
    case "start_blocked":
      return `WakeLoop Service was ${actionPast}, but WakeLoop blocked background startup before it could begin.`;
    case "start_failed":
      return `WakeLoop Service was ${actionPast}, but WakeLoop could not finish starting it.`;
    default: {
      const unexpectedOutcome: never = outcome;
      throw new Error(
        `Unexpected daemon reinstall outcome while building headline text: ${String(
          unexpectedOutcome
        )}`
      );
    }
  }
}

export function buildDaemonReinstallStoppedServiceMessage(
  action: DaemonServiceMaintenanceAction
): string {
  return `Service files were ${formatDaemonServiceMaintenanceActionPast(
    action
  )}, but WakeLoop Service is still stopped. Local Wake work will keep waiting until WakeLoop Service starts.`;
}

export function emitDaemonReinstallResult(input: {
  outcome: DaemonReinstallOutcome;
  presenter: Presenter;
  resolvedView: RuntimeContext["resolvedView"];
  action: DaemonServiceMaintenanceAction;
}): void {
  if (input.resolvedView !== "agent") {
    return;
  }
  input.presenter.data({
    code: "daemon.reinstall.result",
    payload: buildDaemonReinstallAgentPayload({
      action: input.action,
      outcome: input.outcome,
    }),
  });
}

export function buildDaemonReinstallAgentPayload(input: {
  action: DaemonServiceMaintenanceAction;
  outcome: DaemonReinstallOutcome;
}):
  | DaemonReinstallOutcome
  | (DaemonReinstallOutcome & Record<string, unknown>) {
  if (
    input.outcome.ok &&
    input.outcome.result === "completed" &&
    input.outcome.serviceState === "stopped"
  ) {
    return {
      ...input.outcome,
      message: buildDaemonReinstallStoppedServiceMessage(input.action),
      nextSteps: formatWakeLoopCliCommands([
        "wakeloop service start --view agent",
      ]),
      operationalState: "not_running",
      requiresStart: true,
    };
  }
  return input.outcome;
}

export function renderDaemonReinstallCard(input: {
  action: DaemonServiceMaintenanceAction;
  outcome: DaemonReinstallOutcome;
  presenter: Presenter;
  resolvedView: RuntimeContext["resolvedView"];
}): void {
  if (input.resolvedView === "agent") {
    return;
  }
  if (input.outcome.result === "cancelled") {
    return;
  }
  renderServiceActionCard({
    presenter: input.presenter,
    codePrefix: `daemon.reinstall.${input.outcome.result}`,
    title: buildDaemonReinstallResultTitle(input.outcome, input.action),
    rows: [
      {
        label: "✅ Result",
        value: buildDaemonReinstallHeadlineText(input.outcome, input.action),
      },
      {
        label: "ℹ️ Why",
        value: buildDaemonReinstallWhyText(input.outcome, input.action),
      },
      {
        label: "🧩 Current State",
        value: buildDaemonReinstallCurrentStateText(
          input.outcome,
          input.action
        ),
      },
      {
        label: "🔧 Next",
        value: buildDaemonReinstallNextStepText(input.outcome, input.action),
      },
    ],
  });
}

export function renderDaemonReinstallOutcome(input: {
  action: DaemonServiceMaintenanceAction;
  outcome: DaemonReinstallOutcome;
  presenter: Presenter;
  resolvedView: RuntimeContext["resolvedView"];
  suppressHumanResultCard?: boolean;
  json?: boolean;
}): void {
  if (input.json === true) {
    emitCliJsonEnvelope({
      schemaVersion: 1,
      type: "daemon.reinstall",
      ...buildDaemonReinstallAgentPayload({
        action: input.action,
        outcome: input.outcome,
      }),
    });
    return;
  }
  emitDaemonReinstallResult(input);
  if (input.suppressHumanResultCard === true) {
    return;
  }
  renderDaemonReinstallCard(input);
}

export type DaemonDowngradeBlock = {
  installedVersion: string;
  requestedVersion: string;
};

export async function resolveDaemonDowngradeBlock(input: {
  requestedVersion: string;
  readDaemonStatus?: typeof getDaemonStatus;
}): Promise<DaemonDowngradeBlock | null> {
  const readDaemonStatus = input.readDaemonStatus ?? getDaemonStatus;
  const daemonStatus = await readDaemonStatus();
  if (!daemonStatus.installed) {
    return null;
  }

  if (
    !isVersionDowngrade({
      fromVersion: daemonStatus.state.daemonVersion,
      toVersion: input.requestedVersion,
    })
  ) {
    return null;
  }

  return {
    installedVersion: daemonStatus.state.daemonVersion,
    requestedVersion: input.requestedVersion,
  };
}

export function emitDaemonDowngradeBlocked(input: {
  presenter: ReturnType<typeof createPresenter>;
  installedVersion: string;
  requestedVersion: string;
  codePrefix: "daemon.install.blocked" | "daemon.reinstall.blocked";
}): void {
  renderServiceActionCard({
    presenter: input.presenter,
    codePrefix: input.codePrefix,
    title: getDaemonDowngradeBlockedTitle(),
    rows: [
      {
        label: "🏷️ Installed",
        value: `v${input.installedVersion}`,
      },
      {
        label: "💻 This CLI",
        value: `v${input.requestedVersion}`,
      },
      {
        label: "💡 What to do",
        value: formatDaemonServiceDowngradeBlockedRecommendation({
          installedVersion: input.installedVersion,
          expectedVersion: input.requestedVersion,
        }),
      },
      {
        label: "🔍 Next",
        value: formatDaemonDowngradeBlockedNextStep(),
      },
    ],
  });
}

export function emitDaemonInstallDowngradeBlocked(input: {
  presenter: ReturnType<typeof createPresenter>;
  resolvedView: string;
  json: boolean;
  downgradeBlock: { installedVersion: string; requestedVersion: string };
}): void {
  const { downgradeBlock } = input;
  if (input.json) {
    emitCliJsonEnvelope({
      schemaVersion: 1,
      type: "daemon.install",
      status: "downgrade_blocked",
      installedVersion: downgradeBlock.installedVersion,
      requestedVersion: downgradeBlock.requestedVersion,
      upgradeCommand: "wakeloop upgrade",
    });
    return;
  }
  if (input.resolvedView === "agent") {
    input.presenter.data({
      code: "daemon.install.result",
      payload: {
        status: "downgrade_blocked",
        installedVersion: downgradeBlock.installedVersion,
        requestedVersion: downgradeBlock.requestedVersion,
        upgradeCommand: "wakeloop upgrade",
      },
    });
    return;
  }
  emitDaemonDowngradeBlocked({
    presenter: input.presenter,
    installedVersion: downgradeBlock.installedVersion,
    requestedVersion: downgradeBlock.requestedVersion,
    codePrefix: "daemon.install.blocked",
  });
}

export function summarizeDaemonReinstallJournalResult(
  outcome: DaemonReinstallOutcome
): {
  metadata?: Record<string, unknown>;
  result: string;
} {
  return {
    result: outcome.ok ? outcome.result : outcome.result,
    metadata: {
      ok: outcome.ok,
    },
  };
}

export function buildDaemonReinstallUnexpectedStartSummary(
  error: unknown,
  action: DaemonServiceMaintenanceAction = "repair"
): string {
  const actionLabel = formatDaemonServiceMaintenanceActionLabel(action);
  const detail = sanitizeSystemServiceErrorMessage(toErrorMessage(error));
  if (!detail) {
    return `WakeLoop hit a local start error right after service ${actionLabel}.`;
  }
  return `WakeLoop hit a local start error right after service ${actionLabel}: ${detail}`;
}

export function buildDaemonReinstallUnexpectedStartNextStep(
  action: DaemonServiceMaintenanceAction = "repair"
): string {
  return formatInlineWakeLoopCliCommands(
    `Run \`wakeloop service start --mode foreground\` to see the live error. The latest local service log is at \`${getDaemonSystemServiceLogPath()}\`. If it still fails, run \`${formatDaemonServiceMaintenanceCommand(
      action
    )}\` again.`
  );
}

export function buildDaemonReinstallStartBlockedWhyText(
  outcome: Extract<DaemonReinstallOutcome, { result: "start_blocked" }>,
  action: DaemonServiceMaintenanceAction
): string {
  const forcedSuffix = outcome.forceUsed
    ? " Force continue was used after graceful drain timed out."
    : "";
  const actionPast = formatDaemonServiceMaintenanceActionPast(action);
  switch (outcome.blockReason) {
    case "authentication_required":
      return `WakeLoop ${actionPast} WakeLoop Service, but background startup is blocked until this lane signs in to WakeLoop.${forcedSuffix}`;
    case "authentication_environment_mismatch":
      return `WakeLoop ${actionPast} WakeLoop Service, but background startup is blocked because this lane is signed in to a different WakeLoop environment.${forcedSuffix}`;
    case "service_dependencies_unavailable":
      return `WakeLoop ${actionPast} WakeLoop Service, but background startup is blocked because required WakeLoop dependencies are unavailable.${forcedSuffix}`;
    case "cleanup_required":
      return `WakeLoop ${actionPast} WakeLoop Service, but background startup is blocked because WakeLoop still detected conflicting local service state.${forcedSuffix}`;
    default: {
      const unexpectedBlockReason: never = outcome.blockReason;
      throw new Error(
        `Unexpected daemon reinstall start block reason: ${String(unexpectedBlockReason)}`
      );
    }
  }
}

export function buildDaemonReinstallStartBlockedCurrentStateText(
  outcome: Extract<DaemonReinstallOutcome, { result: "start_blocked" }>
): string {
  switch (outcome.blockReason) {
    case "authentication_required":
      return "WakeLoop Service is installed, but it is not running because this lane is not signed in.";
    case "authentication_environment_mismatch":
      return "WakeLoop Service is installed, but it is not running because this lane is signed in to a different WakeLoop environment.";
    case "service_dependencies_unavailable":
      return "WakeLoop Service is installed, but it is not running because required WakeLoop dependencies are unavailable.";
    case "cleanup_required":
      return "WakeLoop Service is installed, but it is not running because WakeLoop still detected conflicting local service state.";
    default: {
      const unexpectedBlockReason: never = outcome.blockReason;
      throw new Error(
        `Unexpected daemon reinstall start block current-state reason: ${String(unexpectedBlockReason)}`
      );
    }
  }
}

export function buildDaemonReinstallStartBlockedNextStep(
  outcome: Extract<DaemonReinstallOutcome, { result: "start_blocked" }>,
  action: DaemonServiceMaintenanceAction
): string {
  switch (outcome.blockReason) {
    case "authentication_required":
      return formatInlineWakeLoopCliCommands(
        `Run \`${formatWakeLoopCliCommand("wakeloop login")}\` first, then \`${formatWakeLoopCliCommand("wakeloop service start")}\` to start WakeLoop Service in the background.`
      );
    case "authentication_environment_mismatch":
      return formatInlineWakeLoopCliCommands(
        `Run \`${formatWakeLoopCliCommand("wakeloop login")}\` for the correct WakeLoop environment, then \`${formatWakeLoopCliCommand("wakeloop service start")}\` again.`
      );
    case "service_dependencies_unavailable":
      return formatInlineWakeLoopCliCommands(
        `Start the required WakeLoop dependencies, then run \`${formatWakeLoopCliCommand("wakeloop service start")}\` again. If you need live detail, run \`${formatWakeLoopCliCommand("wakeloop service start --mode foreground")}\`.`
      );
    case "cleanup_required":
      return formatInlineWakeLoopCliCommands(
        `Run \`${formatWakeLoopCliCommand("wakeloop service status")}\` to inspect the current cleanup guidance. If WakeLoop still reports a conflict, run \`${formatWakeLoopCliCommand(formatDaemonServiceMaintenanceCommand(action))}\` again.`
      );
    default: {
      const unexpectedBlockReason: never = outcome.blockReason;
      throw new Error(
        `Unexpected daemon reinstall start block next-step reason: ${String(unexpectedBlockReason)}`
      );
    }
  }
}

export function buildDaemonReinstallStartFailureNextStep(
  action: DaemonServiceMaintenanceAction
): string {
  return buildDaemonReinstallUnexpectedStartNextStep(action);
}

export function createDaemonReinstallDrainTimeoutResult(
  waitMs: number
): DaemonReinstallDrainTimeoutResult {
  return {
    ok: false,
    result: "drain_timeout",
    forceRequired: true,
    waitMs,
  };
}
